import { useState } from 'react';
import { PropietarioInmueble, TipoCliente, Cliente } from '@/types';
import Button from '@/components/common/Button';
import ClientSearchCard from '@/components/common/ClientSearchCard';
import { getClientFullName } from '@/utils/clientUtils';

interface PropietarioManagerProps {
  propietarios: PropietarioInmueble[];
  onPropietariosChange: (propietarios: PropietarioInmueble[]) => void;
  required?: boolean;
}

export default function PropietarioManager({
  propietarios,
  onPropietariosChange,
  required = false,
}: PropietarioManagerProps) {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedNewClient, setSelectedNewClient] = useState<Cliente | null>(null);

  const handleAddPropietario = () => {
    if (!selectedNewClient) return;

    const newPropietario: PropietarioInmueble = {
      ...selectedNewClient,
      porcentaje_propiedad: 100,
      no_comision: false,
      no_irpf: false,
    };
    onPropietariosChange([...propietarios, newPropietario]);
    setShowAddDialog(false);
    setSelectedNewClient(null);
  };

  const handleRemovePropietario = (id: number) => {
    onPropietariosChange(propietarios.filter((p) => p.id !== id));
  };

  const handleUpdatePropietario = (id: number, updates: Partial<PropietarioInmueble>) => {
    onPropietariosChange(
      propietarios.map((p) => (p.id === id ? { ...p, ...updates } : p))
    );
  };

  const totalPercentage = propietarios.reduce(
    (sum, p) => sum + (p.porcentaje_propiedad || 0),
    0
  );

  return (
    <div className="space-y-4">
      {/* List of propietarios */}
      {propietarios.length > 0 && (
        <div className="space-y-3">
          {propietarios.map((propietario) => (
            <div
              key={propietario.id}
              className="border border-gray-300 rounded-lg p-4 bg-gray-50"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">👤</span>
                  <div>
                    <p className="font-medium text-gray-900">
                      {getClientFullName(propietario)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {propietario.tipo_documento} {propietario.documento}
                    </p>
                  </div>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleRemovePropietario(propietario.id)}
                >
                  Remover
                </Button>
              </div>

              <div className="grid grid-cols-3 gap-4">
                {/* Ownership percentage */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Porcentaje de propiedad (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={propietario.porcentaje_propiedad || 100}
                    onChange={(e) =>
                      handleUpdatePropietario(propietario.id, {
                        porcentaje_propiedad: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* No comision flag */}
                <div className="flex items-center">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={propietario.no_comision || false}
                      onChange={(e) =>
                        handleUpdatePropietario(propietario.id, {
                          no_comision: e.target.checked,
                        })
                      }
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700">
                      No Comisión
                    </span>
                  </label>
                </div>


              </div>
            </div>
          ))}

          {/* Total percentage warning */}
          {totalPercentage !== 100 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                ⚠️ <strong>Advertencia:</strong> El total de porcentajes es {totalPercentage.toFixed(2)}%.
                Se recomienda que sume 100%.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Add propietario button */}
      {!showAddDialog ? (
        <Button variant="secondary" onClick={() => setShowAddDialog(true)}>
          + Agregar Propietario
        </Button>
      ) : (
        <div className="space-y-3">
          <ClientSearchCard
            tipo={TipoCliente.PROPIETARIO}
            selectedClient={selectedNewClient}
            onClientSelect={setSelectedNewClient}
            label="Seleccionar Propietario"
          />
          <div className="flex gap-2">
            <Button
              onClick={handleAddPropietario}
              disabled={!selectedNewClient}
            >
              Agregar
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setShowAddDialog(false);
                setSelectedNewClient(null);
              }}
            >
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {/* Required validation message */}
      {required && propietarios.length === 0 && (
        <p className="text-sm text-red-600">
          * Debe agregar al menos un propietario
        </p>
      )}
    </div>
  );
}
